import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT, GamePhase, ClientGameState } from '@hokm/shared';
import { PERSIAN_THEME } from '../../../theme/persianTheme';

export interface LeaveModalCallbacks {
  onLeave: () => void;
  getGameState: () => ClientGameState | null;
}

/**
 * Leave game confirmation modal.
 * Shows a dialog asking if the player wants to leave, warning that AI will replace them.
 */
export class LeaveModal {
  private scene: Phaser.Scene;
  private modal: Phaser.GameObjects.Container | null = null;
  private callbacks: LeaveModalCallbacks;

  constructor(scene: Phaser.Scene, callbacks: LeaveModalCallbacks) {
    this.scene = scene;
    this.callbacks = callbacks;
  }

  /**
   * Show the leave confirmation modal.
   * Skips confirmation if still in lobby phase.
   */
  show(): void {
    if (this.modal) return;

    // Skip confirmation if still in lobby
    const state = this.callbacks.getGameState();
    if (state?.phase === GamePhase.LOBBY) {
      this.callbacks.onLeave();
      return;
    }

    this.modal = this.scene.add.container(GAME_WIDTH / 2, GAME_HEIGHT / 2);
    this.modal.setDepth(100);

    // Semi-transparent overlay
    const overlay = this.scene.add.rectangle(0, 0, GAME_WIDTH * 2, GAME_HEIGHT * 2, 0x000000, 0.6);
    overlay.setInteractive();

    // Modal box
    const modalBg = this.scene.add
      .rectangle(0, 0, 280, 130, PERSIAN_THEME.colors.tableCenter, 0.98)
      .setStrokeStyle(2, PERSIAN_THEME.colors.gold);

    // Text
    const title = this.scene.add
      .text(0, -35, 'Leave Game?', {
        fontFamily: PERSIAN_THEME.fonts.title,
        fontSize: '22px',
        color: PERSIAN_THEME.hex.gold,
      })
      .setOrigin(0.5);

    const subtitle = this.scene.add
      .text(0, -5, 'Your spot will be taken by AI', {
        fontFamily: PERSIAN_THEME.fonts.body,
        fontSize: '13px',
        color: PERSIAN_THEME.hex.textSecondary,
      })
      .setOrigin(0.5);

    // Leave button
    const yesBtn = this.scene.add.container(-55, 40);
    const yesBg = this.scene.add.rectangle(0, 0, 80, 32, 0x8b3a3a).setStrokeStyle(1, PERSIAN_THEME.colors.bronze);
    const yesText = this.scene.add
      .text(0, 0, 'Leave', {
        fontFamily: PERSIAN_THEME.fonts.body,
        fontSize: '14px',
        color: '#ffffff',
      })
      .setOrigin(0.5);
    yesBtn.add([yesBg, yesText]);
    yesBg.setInteractive({ useHandCursor: true });
    yesBg.on('pointerover', () => yesBg.setFillStyle(0xab4a4a));
    yesBg.on('pointerout', () => yesBg.setFillStyle(0x8b3a3a));
    yesBg.on('pointerdown', () => {
      this.hide();
      this.callbacks.onLeave();
    });

    // Stay button
    const noBtn = this.scene.add.container(55, 40);
    const noBg = this.scene.add
      .rectangle(0, 0, 80, 32, PERSIAN_THEME.colors.jade)
      .setStrokeStyle(1, PERSIAN_THEME.colors.bronze);
    const noText = this.scene.add
      .text(0, 0, 'Stay', {
        fontFamily: PERSIAN_THEME.fonts.body,
        fontSize: '14px',
        color: PERSIAN_THEME.hex.textPrimary,
      })
      .setOrigin(0.5);
    noBtn.add([noBg, noText]);
    noBg.setInteractive({ useHandCursor: true });
    noBg.on('pointerover', () => noBg.setFillStyle(PERSIAN_THEME.colors.buttonHover));
    noBg.on('pointerout', () => noBg.setFillStyle(PERSIAN_THEME.colors.jade));
    noBg.on('pointerdown', () => this.hide());

    this.modal.add([overlay, modalBg, title, subtitle, yesBtn, noBtn]);

    // Fade in
    this.modal.setAlpha(0);
    this.scene.tweens.add({
      targets: this.modal,
      alpha: 1,
      duration: 200,
      ease: 'Power2',
    });
  }

  /**
   * Hide and destroy the modal.
   */
  hide(): void {
    if (!this.modal) return;

    const modal = this.modal;
    this.modal = null;

    this.scene.tweens.add({
      targets: modal,
      alpha: 0,
      duration: 150,
      onComplete: () => modal.destroy(),
    });
  }

  isVisible(): boolean {
    return this.modal !== null;
  }

  destroy(): void {
    this.modal?.destroy();
    this.modal = null;
  }
}
